import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { ResizeSensor } from "@blueprintjs/core";
import classNames from "classnames";
import TextareaScrollbar from "components/TextareaScrollBar";
import TextTruncate from "components/TextTruncate";
import { InputDirection } from "shared/types";
import { mergeRefs } from "utils/data";
import { getTextContentFromHtml, isHtml } from "utils/string";
import { isNotEmpty } from "utils/validate";
import { MAX_NAME_LENGTH, MAX_NOTES_LENGTH } from "store/ps-workspace/ps-workspace.utils";
import useOutsideClick from "hooks/useClickOutside";
import * as styles from "./styles.module.scss";
const inputRefCallback = (input, cursorSelectionStart) => input &&
    isNotEmpty(cursorSelectionStart) &&
    input.setSelectionRange(cursorSelectionStart, cursorSelectionStart);
const TextField = ({ onChange, value, defaultValue, type = "text", textarea = false, maxLabelLength, error, classes = {}, outlined = true, hideRemainingSymbols, rightElement, readonly, suffix, prefix, direction, autoFocus, ...attributes }) => {
    const textAreaRef = useRef(null);
    const inputRef = useRef(null);
    const prefixRef = useRef(null);
    const { isOutside: textAreaIsOutside } = useOutsideClick(textAreaRef);
    const { isOutside: inputIsOutside } = useOutsideClick(inputRef);
    const [cursorSelectionStart, setCursorSelectionStart] = useState();
    const [remainingSymbols, setRemainingSymbols] = useState(0);
    const [hasError, setHasError] = useState(false);
    const [valueAfterChange, setValueAfterChange] = useState();
    const [suffixStyles, setSuffixStyles] = useState();
    const maxLength = useMemo(() => maxLabelLength || (textarea ? MAX_NOTES_LENGTH : MAX_NAME_LENGTH), [maxLabelLength, textarea]);
    const processRemainingSymbols = useCallback((value) => {
        const remainingSymbols = maxLength - (value?.length || 0);
        setRemainingSymbols(remainingSymbols);
        setHasError(remainingSymbols < 0);
    }, [maxLength]);
    const internalOnChange = useCallback((event) => {
        const { value } = event.target;
        !hideRemainingSymbols && processRemainingSymbols(value);
        onChange(event);
    }, [hideRemainingSymbols, onChange, processRemainingSymbols]);
    const handleChange = useCallback((event) => {
        setCursorSelectionStart(event.target.selectionStart ?? 0);
        setValueAfterChange(event.target.value);
        if (value === undefined) {
            internalOnChange(event);
        }
        else {
            onChange(event);
        }
    }, [internalOnChange, onChange, value]);
    const inputProps = useMemo(() => ({
        ...attributes,
        readOnly: readonly,
        ...(value === undefined && defaultValue ? { defaultValue } : { value }),
        maxLength,
        onChange: handleChange,
    }), [attributes, readonly, value, defaultValue, maxLength, handleChange]);
    const handleBlur = useCallback((e) => {
        setCursorSelectionStart(undefined);
        setValueAfterChange(undefined);
        inputProps.onBlur && inputProps.onBlur(e);
    }, [inputProps]);
    const handleKeyUp = useCallback((e) => {
        if (e.key === "Tab") {
            const target = e.target;
            target.setSelectionRange(0, target.value.length);
            inputProps.onKeyUp && inputProps.onKeyUp(e);
        }
    }, [inputProps]);
    const isHtmlPlaceholder = useMemo(() => isHtml(attributes?.placeholder ?? ""), [attributes?.placeholder]);
    const defineSuffixStyles = useCallback((entries) => {
        if (!suffix) {
            return;
        }
        const suffixText = getTextContentFromHtml(suffix) ?? "";
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (isNotEmpty(context) && inputRef.current) {
            const { font, paddingRight, paddingLeft } = getComputedStyle(inputRef.current);
            context.font = font;
            const suffixWidth = context.measureText(` ${suffixText}` ?? "").width;
            if (direction === InputDirection.RIGHT) {
                setSuffixStyles({ suffixWidth: suffixWidth + 4, style: { right: "8px", font } });
            }
            else {
                const valueWidth = context.measureText(`${value} ` ?? "").width;
                const inputValueContainerWidth = entries[0].contentRect.width -
                    Number(paddingRight.replace("px", "")) -
                    Number(paddingLeft.replace("px", ""));
                const suffixLeft = inputValueContainerWidth < valueWidth
                    ? `calc(100% - ${suffixWidth}px)`
                    : `calc(${valueWidth}px + ${paddingLeft})`;
                setSuffixStyles({ suffixWidth, style: { left: suffixLeft, font } });
            }
        }
    }, [direction, suffix, value]);
    useEffect(() => {
        if (hideRemainingSymbols || readonly) {
            return;
        }
        if (value === undefined) {
            processRemainingSymbols(defaultValue || "");
        }
        else {
            processRemainingSymbols(value);
        }
    }, [defaultValue, value, processRemainingSymbols, hideRemainingSymbols, readonly]);
    useEffect(() => {
        if (error) {
            setHasError(true);
        }
    }, [error]);
    useEffect(() => {
        if (inputIsOutside) {
            inputRef.current?.blur();
        }
        if (textAreaIsOutside) {
            textAreaRef.current?.blur();
        }
    }, [inputIsOutside, textAreaIsOutside]);
    useEffect(() => {
        if (valueAfterChange && inputProps.value && valueAfterChange.length !== inputProps.value.length) {
            setCursorSelectionStart(inputProps.value.length);
        }
    }, [inputProps.value, valueAfterChange]);
    useEffect(() => {
        const { current } = inputRef;
        if (current) {
            defineSuffixStyles([{ contentRect: current.getBoundingClientRect() }]);
        }
    }, [defineSuffixStyles, direction, suffix, value]);
    useEffect(() => {
        if (!autoFocus) {
            return;
        }
        if (inputRef?.current) {
            setTimeout(() => inputRef?.current?.focus(), 0);
        }
    }, [autoFocus]);
    return (React.createElement(ResizeSensor, { onResize: defineSuffixStyles },
        React.createElement("div", { className: classNames(styles.textField, classes.container, {
                [styles.error]: hasError,
                [styles.textarea]: textarea,
            }) },
            !!prefix && (React.createElement("span", { className: styles.prefix, ref: prefixRef, dangerouslySetInnerHTML: { __html: prefix } })),
            textarea ? (React.createElement(TextareaScrollbar, { ...inputProps, textareaRef: mergeRefs(textAreaRef, (input) => !defaultValue && inputRefCallback(input, cursorSelectionStart)), onBlur: handleBlur, onKeyUp: handleKeyUp, wrapperClassName: classNames(styles.textareaInputWrapper, classes.input), outlined: outlined, className: classNames(styles.textareaInput, classes.input) })) : (React.createElement(React.Fragment, null,
                React.createElement("input", { ...inputProps, ref: mergeRefs(inputRef, (input) => !defaultValue && inputRefCallback(input, cursorSelectionStart)), onBlur: handleBlur, onKeyUp: handleKeyUp, className: classNames(styles.input, classes.input, { [styles.outlined]: outlined }, { [styles.withSuffix]: !!suffix }, { [styles.placeholderHidden]: isHtmlPlaceholder }), style: {
                        ...(prefix ? { paddingLeft: (prefixRef?.current?.offsetWidth ?? 0) + 7 } : {}),
                        ...(suffix ? { paddingRight: suffixStyles?.suffixWidth } : {}),
                    }, type: type }),
                !isNotEmpty(inputProps.value, true) &&
                    !isNotEmpty(inputProps.defaultValue, true) &&
                    isHtmlPlaceholder && (React.createElement(TextTruncate, { className: styles.htmlPlaceholder, text: attributes?.placeholder ?? "" })))),
            !!suffix && isNotEmpty(value, true) && (React.createElement("span", { className: styles.suffix, style: suffixStyles?.style, dangerouslySetInnerHTML: { __html: suffix } })),
            rightElement && React.createElement("span", { className: styles.rightElement }, rightElement),
            !hideRemainingSymbols && !readonly && (React.createElement("span", { role: "tooltip", className: classNames(styles.remainingSymbols, {
                    [styles["remainingSymbols-textarea"]]: textarea,
                }) },
                "(",
                remainingSymbols,
                ")")),
            error && React.createElement("div", { className: classNames(styles.errorMessage, classes.error) }, error))));
};
export default TextField;
